import { Box, HStack, Flex, Separator } from '@chakra-ui/react';
import { Avatar, UploadAvatar, CameraIcon, tools } from '@halo/ui-chat-react';
import type { User } from '@halo/chat-sdk-react';
import { useAppStore } from '@halo/chat-sdk-react';
import { GenderAge } from '@halo/ui-chat-react';
import QrCodeButton from '@/components/QrCodeButton';

interface HeaderProps {
  user: User;
  handleUpdateProfileUser: () => void;
  countryLabel: string;
}

const Header = ({ user, handleUpdateProfileUser, countryLabel }: HeaderProps) => {
  const { client } = useAppStore();

  const handleUploadAvatar = useCallback(
    async (file: File) => {
      await client?.Api.user.uploadAvatar({ file });
      handleUpdateProfileUser();
    },
    [client?.Api.user, handleUpdateProfileUser]
  );

  return (
    <Box bg="bg.subtle" w="full" roundedTopLeft="2xl" roundedTopRight="2xl" pb="2.5">
      <HStack w="full" px="4" gap="4" roundedTopLeft="2xl" roundedTopRight="2xl" mt="-2.5">
        <Box w={86} height={86} marginTop="-14px">
          <UploadAvatar onChange={handleUploadAvatar}>
            <Box
              pos="relative"
              cursor="pointer"
              css={{
                '&:hover': {
                  '--show-update-avatar': 'flex',
                },
              }}
            >
              <Avatar
                css={{
                  outlineWidth: '3px',
                  outlineColor: 'bg',
                  outlineStyle: 'solid',
                }}
                src={user?.avatar as string}
                name={user?.name}
                w={86}
                height={86}
              />
              <Flex
                pos="absolute"
                top={0}
                left={0}
                alignItems="center"
                justifyContent="center"
                zIndex={10}
                w={86}
                height={86}
                bg="blackAlpha.70"
                rounded="50%"
                display="var(--show-update-avatar, none)"
              >
                <CameraIcon width="24px" height="24px" />
              </Flex>
            </Box>
          </UploadAvatar>
        </Box>
        <Box flex="1" overflow="hidden">
          <Flex justifyContent="space-between" alignItems="center">
            <Flex alignItems="center" gap="1" minW="0" flex={1} overflow={'hidden'}>
              <Box
                truncate
                minW="0"
                fontWeight="semibold"
                fontSize="xl"
                lineHeight="shorter"
                cursor="pointer"
                maxW="80%"
                onClick={() => tools.copyToClipboard(user?.name)}
              >
                {user?.name}
              </Box>
              <GenderAge gender={user?.gender} age={user?.birthday} />
            </Flex>
            <QrCodeButton user={user} source="user" />
          </Flex>

          <Flex alignItems="center" fontSize="sm" color="fg.muted" fontWeight="medium">
            <Box
              minW="50px"
              truncate
              onClick={() => tools.copyToClipboard(`@${user?.username}`)}
              cursor="pointer"
            >
              @{user?.username}
            </Box>
            {user?.location && (
              <>
                <Separator h="1em" orientation="vertical" mx="1.5" borderColor="border.muted" />
                <Box truncate>{countryLabel}</Box>
              </>
            )}
          </Flex>
        </Box>
      </HStack>
    </Box>
  );
};

const MemoizedHeader = memo(Header);
export { MemoizedHeader as Header };
